import { Injectable, type Provider } from '@angular/core';

import { ok, type ApiResult } from '../../../core/models/api-result';
import type { HubCreateDraft, HubCreateOutcome } from './hub-create.models';
import { HubCreateService } from './hub-create.service';
import { HubCreateCoverService } from './hub-create-cover.service';
import type { CoverImageStyleId } from '../../../shared/media/image-prompt';
import { HubInterestsService } from './hub-interests.service';
import { HubPeopleService } from './hub-people.service';

/**
 * STARTING A HUB, WITHOUT A SESSION — the design and e2e harness for
 * `/hubs/new`, served at `/preview/hubs/new`.
 *
 * WHY IT EXISTS. Every read behind that page is viewer-scoped:
 * `interestCatalogue` and `employees` both 401 unauthenticated, so the real
 * route renders a subject picker with no subjects and a people search that
 * finds nobody — which looks like a broken page rather than an absent
 * session. The same reasoning, and the same shape, as
 * `HubDirectoryPreviewService` beside it.
 *
 * ⚠ THE WRITE SEAM IS THE IMPORTANT ONE. The other preview services are all
 * read-side; this one has to stop `createHub` reaching a real API, because
 * the page's whole third act is what happens AFTER the press — the
 * confirmation, the three routes out, and the partial-failure list. Without a
 * fake create, the only reviewable part of the flow is the two steps before
 * it.
 *
 * ⚠ IT DOES NOT PRETEND TO SUCCEED ALWAYS. A hub named with the word "fail"
 * comes back with follow-up failures attached, because the partial-failure
 * branch is the one nobody would otherwise ever look at — and it is the
 * branch where getting the copy wrong sends somebody off to make a second
 * hub.
 */

/**
 * THE TAXONOMY, AT THE SHAPE IT ACTUALLY ARRIVES IN.
 *
 * ⚠ THIS USED TO BE ELEVEN INTERESTS IN FOUR CATEGORIES, and that was not a
 * smaller version of production — it was a DIFFERENT surface. The live
 * catalogue is 67 interests across nine categories, which is 673px of chips
 * in a 320px box; the eleven fitted in 238px and never scrolled. So the
 * harness could not show the picker's scroll cue at all, and the e2e written
 * for it failed against a fixture that had nothing to scroll.
 *
 * A harness that cannot reproduce the real thing's proportions cannot catch
 * the defects that come from them. The categories and their order are the
 * live ones, read off the API on 7 September 2026.
 */
@Injectable()
export class HubInterestsPreviewService extends HubInterestsService {
  override async load(): Promise<void> {
    this['setGroups'](
      [
        ['sport', 'Sport & Fitness', false, [
          'Running', 'Cycling', 'Football', 'Padel & racquet', 'Gym & strength',
          'Swimming', 'Yoga & Pilates', 'Climbing', 'Golf', 'Walking & hiking',
          'Team leagues',
        ]],
        ['wellbeing', 'Wellbeing', false, [
          'Mental health', 'Mindfulness', 'Sleep', 'Nutrition',
          'Menopause support', 'Parents & carers', 'Neurodiversity',
          'Movement & physio',
        ]],
        ['food', 'Food & Drink', false, [
          'Coffee', 'Tea', 'Baking', 'Cooking', 'Lunch spots', 'Plant-based',
        ]],
        ['learning', 'Learning & Career', false, [
          'Mentoring', 'Public speaking', 'Languages', 'Book club',
          'Lunch & learn', 'Qualifications', 'Leadership',
        ]],
        ['tech', 'Technology & Making', false, [
          'Photography', 'Coding', '3D printing', 'Electronics', 'AI & data',
          'Woodwork', 'Repair café',
        ]],
        ['creative', 'Creative', false, [
          'Music', 'Drawing & painting', 'Writing', 'Film', 'Craft', 'Design',
        ]],
        ['sustainability', 'Sustainability & Giving', false, [
          'Volunteering', 'Fundraising', 'Recycling', 'Cycle to work',
          'Local nature',
        ]],
        ['social', 'Social', false, [
          'Board games', 'Quizzes', 'Pub & socials', 'Travel', 'Pets',
          'Gardening',
        ]],
        // ⚠ ONE SENSITIVE GROUP ON PURPOSE. Networks is where the GDPR Art. 9
        // categories live, and a preview without one cannot show that the
        // picker offers them and the analytics never names them.
        ['networks', 'Networks', true, [
          "Women's network", 'Carers', 'Pride', 'Ethnicity & heritage',
          'Disability & access', 'Faith',
        ]],
      ].map(([key, label, sensitive, labels]) => ({
        key: key as string,
        label: label as string,
        sensitive: sensitive as boolean,
        interests: (labels as string[]).map((name) => ({
          key: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
          label: name,
          categoryKey: key as string,
          categoryLabel: label as string,
          sensitive: sensitive as boolean,
        })),
      })),
    );
  }
}

/** A directory of eleven, which is what the picker has to work at. */
const PREVIEW_PEOPLE = [
  { id: 'p1', name: 'Dean Okafor', jobTitle: 'Workplace experience' },
  { id: 'p2', name: 'Priya Raman', jobTitle: 'Product design' },
  { id: 'p3', name: 'Marcus Webb', jobTitle: 'Facilities lead' },
  { id: 'p4', name: 'Sofia Almeida', jobTitle: 'Internal communications' },
  { id: 'p5', name: 'Sam Ellis', jobTitle: 'Software engineer' },
  { id: 'p6', name: 'Aisha Bello', jobTitle: 'People partner' },
  { id: 'p7', name: 'Tom Bright', jobTitle: 'Data analyst' },
  { id: 'p8', name: 'Anya Kaur', jobTitle: 'Service designer' },
  { id: 'p9', name: 'Joel Hartley', jobTitle: 'Workplace technology' },
  { id: 'p10', name: 'Nina Costa', jobTitle: 'Sustainability lead' },
  { id: 'p11', name: 'Ravi Shah', jobTitle: 'Finance business partner' },
];

/** The harness's colleagues by id, for surfaces that add one and must name them. */
export const PREVIEW_PERSON_NAMES: Readonly<Record<string, string>> = Object.fromEntries(
  PREVIEW_PEOPLE.map(p => [p.id, p.name]),
);

@Injectable()
export class HubPeoplePreviewService extends HubPeopleService {
  /**
   * Name first, then job title — the same order the server ranks in, so the
   * preview does not teach a behaviour the real page does not have.
   */
  override async search(term: string, take = 6) {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return ok([]);
    return ok(
      PREVIEW_PEOPLE.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.jobTitle.toLowerCase().includes(q),
      )
        .slice(0, take)
        .map((p) => ({ ...p, picture: null })),
    );
  }
}

@Injectable()
export class HubCreatePreviewService extends HubCreateService {
  override async create(
    draft: HubCreateDraft,
  ): Promise<ApiResult<HubCreateOutcome>> {
    const wantsFailure = /fail/i.test(draft.name);
    // A beat, so the press reads as pressed, then busy, then done — the real
    // create is four round trips, and an instant preview would hide the busy
    // state the page has to draw.
    await new Promise((resolve) => setTimeout(resolve, PREVIEW_CREATE_MS));
    const members = draft.members.filter((m) => !draft.moderators.some((x) => x.id === m.id));
    return ok({
      hubId: 'preview-hub',
      visibility: 'OPEN',
      // The harness keeps every cover (they are addresses already).
      coverImage: draft.coverUrl,
      invitesSupported: true,
      invited: members.length || null,
      invite: members.length
        ? { invited: members.length, alreadyMembers: 0, alreadyInvited: 0, skipped: 0, invites: [] }
        : null,
      followUpFailures: wantsFailure
        ? [
            {
              kind: 'moderator' as const,
              who: draft.moderators[0]?.name ?? 'Sam Ellis',
              message: 'That person has left the organisation.',
            },
          ]
        : [],
    });
  }

  override async joinHub(): Promise<ApiResult<boolean>> {
    return ok(true);
  }
}

/** How long the preview's create takes. Long enough to see the busy state. */
const PREVIEW_CREATE_MS = 900;

/**
 * Covers from the harness's own photographs, a few at a time, so the tiles
 * show their skeleton and then sharpen as each one lands — the real draw is
 * an image model, seconds per candidate.
 *
 * ⚠ ADDRESSES, NOT `data:` URLs. The create path uploads only `data:` URLs,
 * so a preview cover goes straight through as an address, as a real
 * already-kept picture would.
 */
const PREVIEW_COVERS: Readonly<Record<'photo' | 'other', readonly string[]>> = {
  photo: [
    '/assets/hubs-preview/snap-coffee-queue.jpg',
    '/assets/hubs-preview/snap-roof-terrace.jpg',
    '/assets/hubs-preview/pro-team.jpg',
    '/assets/hubs-preview/snap-cake.jpg',
    '/assets/hubs-preview/pro-atrium.jpg',
    '/assets/hubs-preview/snap-pub.jpg',
  ],
  other: [
    '/assets/hubs-preview/ai-gradient.jpg',
    '/assets/hubs-preview/ai-leaf.jpg',
    '/assets/hubs-preview/ai-city.jpg',
    '/assets/hubs-preview/ai-network.jpg',
  ],
};

const PHOTO_STYLES: ReadonlySet<CoverImageStyleId> = new Set(['photographic', 'editorial', 'monochrome']);

/** Per-candidate delay: the first lands quickly, the third a beat later. */
const PREVIEW_DRAW_MS = [500, 800, 1100];

@Injectable()
export class HubCreateCoverPreviewService extends HubCreateCoverService {
  private round = 0;

  override async draw(_prompt: string, style: CoverImageStyleId, index: number): Promise<ApiResult<string>> {
    if (index === 0) this.round++;
    await new Promise((resolve) => setTimeout(resolve, PREVIEW_DRAW_MS[index % PREVIEW_DRAW_MS.length]));
    const pool = PHOTO_STYLES.has(style) ? PREVIEW_COVERS.photo : PREVIEW_COVERS.other;
    return ok(pool[((this.round - 1) * 3 + index) % pool.length]);
  }
}

export const HUB_CREATE_PREVIEW_PROVIDERS: Provider[] = [
  { provide: HubCreateCoverService, useClass: HubCreateCoverPreviewService },
  { provide: HubInterestsService, useClass: HubInterestsPreviewService },
  { provide: HubPeopleService, useClass: HubPeoplePreviewService },
  { provide: HubCreateService, useClass: HubCreatePreviewService },
];
